import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Edit, Trash2, FileQuestion, Clock, Users, ArrowLeft, CheckCircle, XCircle, Award, BarChart2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/api/api';
import { formatDate } from '../../utils/dateTimeUtils';

const QuizList = () => {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCourse, setFilterCourse] = useState('all');
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedQuizResults, setSelectedQuizResults] = useState([]);
  const [quizStats, setQuizStats] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    course_id: '',
    duration_minutes: 30,
    pass_percentage: 40,
    max_attempts: 1,
    shuffle_questions: true,
    show_answers_after: true,
    questions: []
  });
  
  const [currentQuestion, setCurrentQuestion] = useState({
    question: '',
    options: ['', '', '', ''],
    correct_option: 0,
    marks: 1,
    explanation: ''
  });

  useEffect(() => {
    fetchData();
  }, [filterCourse]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [quizzesRes, coursesRes] = await Promise.all([
        api.get('/lms/quizzes', { 
          params: { course_id: filterCourse !== 'all' ? filterCourse : undefined } 
        }),
        api.get('/lms/teacher/courses')
      ]);
      
      setQuizzes(quizzesRes.data || []);
      setCourses(coursesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load quizzes');
    } finally {
      setLoading(false);
    }
  };

  const addQuestion = () => {
    if (!currentQuestion.question || currentQuestion.options.some(o => !o.trim())) {
      toast.error('Please fill in question and all options');
      return;
    }
    
    setFormData({
      ...formData,
      questions: [...formData.questions, { ...currentQuestion }]
    });
    
    setCurrentQuestion({
      question: '',
      options: ['', '', '', ''],
      correct_option: 0,
      marks: 1,
      explanation: ''
    });
    
    toast.success('Question added');
  };

  const removeQuestion = (index) => {
    setFormData({
      ...formData,
      questions: formData.questions.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.course_id) {
      toast.error('Please fill in quiz title and select course');
      return;
    }
    
    if (formData.questions.length === 0) {
      toast.error('Please add at least one question');
      return;
    }

    try {
      if (editingQuiz) {
        await api.put(`/lms/quizzes/${editingQuiz.id}`, formData);
        toast.success('Quiz updated successfully');
      } else {
        await api.post('/lms/quizzes', formData);
        toast.success('Quiz created successfully');
      }
      
      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving quiz:', error);
      toast.error(error.response?.data?.detail || 'Failed to save quiz');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this quiz?')) return;
    
    try {
      await api.delete(`/lms/quizzes/${id}`);
      toast.success('Quiz deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete quiz');
    }
  };

  const viewResults = async (quiz) => {
    try {
      const [attemptsRes, statsRes] = await Promise.all([
        api.get(`/lms/quizzes/${quiz.id}/attempts`),
        api.get('/lms/stats/quiz-performance', { params: { quiz_id: quiz.id } })
      ]);
      
      setSelectedQuizResults(attemptsRes.data || []);
      setQuizStats(statsRes.data);
      setShowResultsModal(true);
    } catch (error) {
      toast.error('Failed to load results');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      course_id: '',
      duration_minutes: 30,
      pass_percentage: 40,
      max_attempts: 1,
      shuffle_questions: true,
      show_answers_after: true,
      questions: []
    });
    setCurrentQuestion({
      question: '',
      options: ['', '', '', ''],
      correct_option: 0,
      marks: 1,
      explanation: ''
    });
    setEditingQuiz(null);
  };

  const openEditModal = async (quiz) => {
    try {
      const res = await api.get(`/lms/quizzes/${quiz.id}?include_answers=true`);
      const fullQuiz = res.data;
      
      setEditingQuiz(fullQuiz);
      setFormData({
        title: fullQuiz.title || '',
        description: fullQuiz.description || '',
        course_id: fullQuiz.course_id || '',
        duration_minutes: fullQuiz.duration_minutes || 30,
        pass_percentage: fullQuiz.pass_percentage || 40,
        max_attempts: fullQuiz.max_attempts || 1,
        shuffle_questions: fullQuiz.shuffle_questions !== false,
        show_answers_after: fullQuiz.show_answers_after !== false,
        questions: fullQuiz.questions || []
      });
      setIsModalOpen(true);
    } catch (error) {
      toast.error('Failed to load quiz details');
    }
  };

  return (
    <div className="space-y-6" data-testid="lms-quiz-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/lms')} size="sm">
            <ArrowLeft size={18} className="mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Quizzes & Tests</h1>
            <p className="text-gray-600">Create MCQ tests with auto-grading</p>
          </div>
        </div>
        <Button 
          onClick={() => { resetForm(); setIsModalOpen(true); }}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="create-quiz-btn"
        >
          <Plus size={18} className="mr-2" /> Create Quiz
        </Button>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <Select value={filterCourse} onValueChange={setFilterCourse}>
            <SelectTrigger className="w-[250px]">
              <SelectValue placeholder="Filter by Course" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Courses</SelectItem>
              {courses.map(course => (
                <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Quiz List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : quizzes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileQuestion className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500">No quizzes found. Create your first quiz!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {quizzes.map((quiz) => (
            <Card key={quiz.id} data-testid={`quiz-${quiz.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-indigo-100 rounded-lg">
                      <FileQuestion className="text-indigo-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{quiz.title}</h3>
                      <p className="text-sm text-gray-500">{quiz.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <FileQuestion size={12} />
                          {quiz.total_questions} questions
                        </span>
                        <span className="flex items-center gap-1">
                          <Award size={12} />
                          {quiz.total_marks} marks
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {quiz.duration_minutes} min
                        </span>
                        <span className="flex items-center gap-1">
                          <Users size={12} />
                          {quiz.attempts_count || 0} attempts
                        </span>
                        <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                          Pass: {quiz.pass_percentage}%
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => viewResults(quiz)}>
                      <BarChart2 size={16} className="mr-1" /> Results
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openEditModal(quiz)}>
                      <Edit size={16} />
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(quiz.id)}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Quiz Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingQuiz ? 'Edit Quiz' : 'Create New Quiz'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm font-medium">Quiz Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., Chapter 1 - Variables and Data Types"
                  required
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description"
                  rows={2}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Course *</label>
                <Select 
                  value={formData.course_id} 
                  onValueChange={(val) => setFormData({...formData, course_id: val})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Course" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map(course => (
                      <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Duration (minutes)</label>
                <Input
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value) || 30})}
                  min={5}
                  max={180}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Pass Percentage</label>
                <Input
                  type="number"
                  value={formData.pass_percentage}
                  onChange={(e) => setFormData({...formData, pass_percentage: parseInt(e.target.value) || 40})}
                  min={0}
                  max={100}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Max Attempts</label>
                <Input
                  type="number"
                  value={formData.max_attempts}
                  onChange={(e) => setFormData({...formData, max_attempts: parseInt(e.target.value) || 1})}
                  min={1}
                  max={10}
                />
              </div>
            </div>
            
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.shuffle_questions}
                  onChange={(e) => setFormData({...formData, shuffle_questions: e.target.checked})}
                  className="rounded"
                />
                <span className="text-sm">Shuffle questions</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.show_answers_after}
                  onChange={(e) => setFormData({...formData, show_answers_after: e.target.checked})}
                  className="rounded"
                />
                <span className="text-sm">Show answers after submission</span>
              </label>
            </div>

            {/* Questions Section */}
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-4">Questions ({formData.questions.length})</h3>
              
              {/* Added Questions */}
              {formData.questions.length > 0 && (
                <div className="space-y-2 mb-4">
                  {formData.questions.map((q, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-sm">Q{idx + 1}: {q.question}</p>
                        <p className="text-xs text-gray-500">
                          Correct: Option {q.correct_option + 1} • {q.marks} mark(s)
                        </p>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeQuestion(idx)}>
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add New Question Form */}
              <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg space-y-3">
                <p className="text-sm font-medium text-gray-600">Add New Question</p>
                <Input
                  value={currentQuestion.question}
                  onChange={(e) => setCurrentQuestion({...currentQuestion, question: e.target.value})}
                  placeholder="Enter question"
                />
                <div className="grid grid-cols-2 gap-2">
                  {currentQuestion.options.map((opt, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="correct"
                        checked={currentQuestion.correct_option === idx}
                        onChange={() => setCurrentQuestion({...currentQuestion, correct_option: idx})}
                        className="text-green-600"
                      />
                      <Input
                        value={opt}
                        onChange={(e) => {
                          const newOptions = [...currentQuestion.options];
                          newOptions[idx] = e.target.value;
                          setCurrentQuestion({...currentQuestion, options: newOptions});
                        }}
                        placeholder={`Option ${idx + 1}`}
                        className={currentQuestion.correct_option === idx ? 'border-green-500' : ''}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex gap-4 items-center">
                  <div className="flex-1">
                    <Input
                      value={currentQuestion.explanation}
                      onChange={(e) => setCurrentQuestion({...currentQuestion, explanation: e.target.value})}
                      placeholder="Explanation (optional)"
                    />
                  </div>
                  <div className="w-24">
                    <Input
                      type="number"
                      value={currentQuestion.marks}
                      onChange={(e) => setCurrentQuestion({...currentQuestion, marks: parseInt(e.target.value) || 1})}
                      min={1}
                      placeholder="Marks"
                    />
                  </div>
                  <Button type="button" onClick={addQuestion} className="bg-green-600 hover:bg-green-700">
                    <Plus size={16} className="mr-1" /> Add
                  </Button>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                {editingQuiz ? 'Update' : 'Create'} Quiz
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Results Modal */}
      <Dialog open={showResultsModal} onOpenChange={setShowResultsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Quiz Results</DialogTitle>
          </DialogHeader>
          
          {quizStats && (
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-700">{quizStats.total_attempts}</p>
                <p className="text-xs text-gray-600">Attempts</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-700">{quizStats.average_score}%</p>
                <p className="text-xs text-gray-600">Average</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-purple-700">{quizStats.pass_rate}%</p>
                <p className="text-xs text-gray-600">Pass Rate</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-orange-700">{quizStats.highest_score}%</p>
                <p className="text-xs text-gray-600">Highest</p>
              </div>
            </div>
          )}
          
          <div className="max-h-[400px] overflow-y-auto">
            {selectedQuizResults.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No attempts yet</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2">Student</th>
                    <th className="text-center p-2">Score</th>
                    <th className="text-center p-2">Percentage</th>
                    <th className="text-center p-2">Status</th>
                    <th className="text-center p-2">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedQuizResults.map((attempt, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="p-2">{attempt.user_name}</td>
                      <td className="text-center p-2">{attempt.score}/{attempt.total_marks || '-'}</td>
                      <td className="text-center p-2">{attempt.percentage}%</td>
                      <td className="text-center p-2">
                        {attempt.passed ? (
                          <span className="inline-flex items-center gap-1 text-green-600">
                            <CheckCircle size={14} /> Passed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-600">
                            <XCircle size={14} /> Failed
                          </span>
                        )}
                      </td>
                      <td className="text-center p-2 text-gray-500">
                        {formatDate(attempt.completed_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuizList;
